// Layout pieces shared by every Settings section, composed from the ui
// primitives: the section heading (h2 + at most one short line, from the registry), a
// solid settings card (h3, one line, a count or action on the right) and
// a labelled field (label, help, error).
//
// Cards are the shared Card minus its backdrop blur: they sit on a flat
// page, so the blur adds nothing, and in Glass each blurred card became
// its own compositor layer.

import { createContext, useContext, useId, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { LoadError } from "@/components/ui/LoadError";
import { Skeleton } from "@/components/ui/Skeleton";
import { cx } from "@/components/ui/controlStyles";

import { sectionMeta, type SectionKey } from "./sections";

/**
 * For a TokenInput: once a list has a chip, the add field keeps to one row
 * beside it on a phone (the field's own 120px minimum pushed it onto an
 * empty second line under a long address). No "press Enter" line under a
 * list: the TokenInput shows its own hint while typing, and commits on
 * Enter, comma, Tab and blur.
 */
export const TOKEN_FIELD = "[&_input]:min-w-[56px]";

/** The id the page focuses after a section switch. */
export const SECTION_HEADING_ID = "settings-section-heading";

/**
 * True where the page shows its sections as tabs (a phone, a narrow
 * window): the selected tab already names the section, so the section's
 * own heading is kept for screen readers and focus only.
 */
export const SettingsTabsShown = createContext(false);

export function SettingsSection({
  section,
  actions,
  children,
}: {
  section: SectionKey;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const meta = sectionMeta(section);
  const tabs = useContext(SettingsTabsShown);
  const bare = tabs && !meta.description && !actions;
  return (
    <section aria-labelledby={SECTION_HEADING_ID} className="flex min-w-0 flex-col">
      <div className={cx("flex flex-wrap items-start justify-between gap-x-6 gap-y-3", !bare && "mb-5")}>
        <div className="min-w-0 flex-[1_1_300px]">
          <h2
            id={SECTION_HEADING_ID}
            tabIndex={-1}
            className={cx("text-xl font-semibold text-fg outline-none", tabs && "sr-only")}
          >
            {meta.label}
          </h2>
          {meta.description && <p className={cx("text-base text-fg-muted [text-wrap:pretty]", !tabs && "mt-1")}>{meta.description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className="flex min-w-0 flex-col gap-4">{children}</div>
    </section>
  );
}

export function SettingsCard({
  title,
  description,
  aside,
  children,
  className,
  titleId,
}: {
  title?: ReactNode;
  description?: ReactNode;
  /** Right of the title: a count, a status or a small action. */
  aside?: ReactNode;
  children?: ReactNode;
  className?: string;
  titleId?: string;
}) {
  // A count ("1 address") sits on the title's baseline; a button or badge
  // is top-aligned and pulled up so it doesn't make the row taller.
  const textAside = typeof aside === "string" || typeof aside === "number";
  return (
    <Card className={cx("flex flex-col gap-4 p-5 [backdrop-filter:none] max-sm:p-4", className)}>
      {(title || description || aside) && (
        <div className="min-w-0">
          {(title || aside) && (
            <div className={cx("flex min-h-[20px] justify-between gap-x-4", textAside ? "items-baseline" : "items-start")}>
              {title && (
                <h3 id={titleId} className="min-w-0 text-base font-semibold text-fg">
                  {title}
                </h3>
              )}
              {aside && (
                <div
                  className={cx(
                    "ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-fg-subtle",
                    !textAside && "-my-1 py-0.5",
                  )}
                >
                  {aside}
                </div>
              )}
            </div>
          )}
          {description && <p className="mt-1 text-sm text-fg-muted [text-wrap:pretty]">{description}</p>}
        </div>
      )}
      {children}
    </Card>
  );
}

/**
 * A labelled control. `children` receives the id to put on the control
 * and the ids that describe it (help + error), for aria-describedby.
 */
export function Field({
  label,
  help,
  error,
  children,
  className,
}: {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cx("flex min-w-0 flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {label}
      </label>
      {children({ id, describedBy })}
      {help && (
        <p id={helpId} className="text-xs text-fg-subtle">
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** A "Details" disclosure for technical detail behind a plain sentence. */
export function MoreDetails({ label = "Details", children }: { label?: string; children: ReactNode }) {
  return (
    <details className="group">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-control text-sm text-fg-muted outline-none [@media(hover:hover)]:hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus [&::-webkit-details-marker]:hidden">
        <ChevronRight aria-hidden className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
        {label}
      </summary>
      <div className="mt-2 rounded-control bg-inset p-3 text-sm text-fg-muted [overflow-wrap:anywhere]">{children}</div>
    </details>
  );
}

/** Placeholder rows while a section's query loads. */
export function SettingsLoading({ rows = 2 }: { rows?: number }) {
  return (
    <div aria-busy className="flex flex-col gap-4">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <Card key={i} className="flex flex-col gap-3 p-5 [backdrop-filter:none]">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-9 w-full" />
        </Card>
      ))}
    </div>
  );
}

/**
 * A load failure in place of a section's cards: "Couldn't load
 * Integrations", a sentence that follows the kind of failure, the raw
 * text folded behind Details, and Try again (a spinner while it
 * refetches). A 401 is left to the app's sign-out handling.
 */
export function SettingsLoadError({
  label,
  error,
  onRetry,
  retrying = false,
}: {
  label: string;
  error: unknown;
  onRetry: () => void;
  retrying?: boolean;
}) {
  return (
    <Card className="[backdrop-filter:none]">
      <LoadError what={label} error={error} onRetry={onRetry} retrying={retrying} />
    </Card>
  );
}
